import { type ColumnDef } from "@tanstack/react-table";
import { PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { DataTable } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RemoteCombobox } from "@/components/ui/remote-combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageContainer, PageHeader } from "@/components/layout";
import AddFlowDialog from "../../components/flows/add-flow-dialog";
import { ConfirmDialog } from "../../components/confirm-dialog";
import { FlowSheet } from "../../components/flows/flow-sheet";
import type { FlowRowForSheet } from "../../components/flows/flow-sheet";
import FiltersCard from "../../components/filters-card";
import { TagsMultiSelect } from "../../components/tags-multi-select";
import { useQueryState } from "../../utils/query-state";
import { FlowTypes } from "../../entities/flow-type";
import { Permissions } from "../../entities/permissions";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import { hasPermission } from "../login/selectors";
import {
  useDeleteFlowMutation,
  useFlowListQuery,
} from "../../services/api";

interface FlowType {
  name?: string;
  translatedName?: string;
}

interface TagRef {
  id: number;
  name: string;
  color: string | null;
}

interface FlowRow {
  id: string;
  name: string;
  flowType: FlowType;
  tags?: TagRef[];
}

interface FlowsResponse {
  items?: FlowRow[];
  itemCount?: number;
  range?: { limit?: number; page?: number };
}

interface AutocompleteValue {
  id?: string | number;
  [key: string]: unknown;
}

export default function Flows() {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const hasAddPermission = useSelector(
    hasPermission(Permissions.PAGE_FLOWS_ADD)
  );
  const hasEditPermission = useSelector(
    hasPermission(Permissions.PAGE_FLOWS_EDIT)
  );
  const hasRemovePermission = useSelector(
    hasPermission(Permissions.PAGE_FLOWS_REMOVE)
  );

  const [openAddFlowDialog, setOpenAddFlowDialog] = useState(false);
  const [openDeleteFlowDialog, setOpenDeleteFlowDialog] = useState(false);

  // Sheet state in URL so refresh / shared links restore it.
  const [selectedDetailId, setSelectedDetailId] = useQueryState({
    paramName: "detail",
    fromQuery: (value: string | null) => value || null,
    toQuery: (value: string | null) => (value ? String(value) : ""),
    defaultValue: null,
  }) as [string | null, (next: string | null) => void];
  const [editOnOpen, setEditOnOpen] = useState(false);

  // SeeFlowDialog / EditFlowDialog read `flow.items` (an array of child
  // items) on render and crash on undefined.map — so unlike the
  // EditResourceDialog pattern this state must stay `null` and the dialogs
  // mount only when a flow is selected.
  const [selectedFlow, setSelectedFlow] = useState<FlowRow | null>(null);

  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<
    Record<string, string | number[] | undefined> | null
  >(null);

  // Filter form state.
  const [flow, setFlow] = useState<AutocompleteValue | null>(null);
  const [flowType, setFlowType] = useState<string>("");
  const [filterTags, setFilterTags] = useState<TagRef[]>([]);
  const [clearKey, setClearKey] = useState(0);

  const computedFilters = useMemo(() => {
    const obj: Record<string, string | number[] | undefined> = {
      id: flow?.id != null ? String(flow.id) : undefined,
      flowTypeId: flowType || undefined,
      tagIds: filterTags.length > 0 ? filterTags.map((tag) => tag.id) : undefined,
    };
    Object.keys(obj).forEach((key) => {
      if (obj[key] === undefined) delete obj[key];
    });
    return obj;
  }, [flow, flowType, filterTags]);

  function onSearch() {
    setFilters(computedFilters);
    setPage(0);
  }

  function onCancel() {
    setFlow(null);
    setFlowType("");
    setFilterTags([]);
    setFilters(null);
    setPage(0);
    setClearKey((k) => k + 1);
  }

  const { data: flows, isFetching: fetchingList } = useFlowListQuery({
    range: { page: page + 1 },
    filters: filters ?? undefined,
  }) as { data?: FlowsResponse; isFetching: boolean };
  const [deleteFlow, { isLoading: isDeleting }] = useDeleteFlowMutation();

  // Normalise flowType name → translatedName for display. Same logic the
  // original consumer ran on every fetch; pulled out as a memoised
  // transformation so the table receives the prepared rows.
  const rows = useMemo<FlowRow[]>(() => {
    if (!flows?.items) return [];
    return flows.items.map((row) => {
      const next = { ...row, flowType: { ...row.flowType } };
      if (next.flowType.name === FlowTypes.FREE) {
        next.flowType.translatedName = t(
          "flows.add-flow-dialog.select-type.free"
        );
      } else if (next.flowType.name === FlowTypes.SEQUENTIAL) {
        next.flowType.translatedName = t(
          "flows.add-flow-dialog.select-type.sequencial"
        );
      }
      return next;
    });
  }, [flows, t]);

  function onSee(row: FlowRow) {
    setSelectedFlow(row);
    setEditOnOpen(false);
    setSelectedDetailId(String(row.id));
  }

  function onEdit(row: FlowRow) {
    setSelectedFlow(row);
    setEditOnOpen(true);
    setSelectedDetailId(String(row.id));
  }

  function onDelete(row: FlowRow) {
    setSelectedFlow(row);
    setOpenDeleteFlowDialog(true);
  }

  async function onDeleted() {
    if (!selectedFlow?.id) {
      showNotification("error", t("common.api-error"));
      setOpenDeleteFlowDialog(false);
      return;
    }
    try {
      await deleteFlow({ id: selectedFlow.id }).unwrap();
      showNotification(
        "success",
        t("flows.delete-flow-dialog.success", { name: selectedFlow.name }),
      );
      // Close the sheet if it was open on the deleted row.
      if (selectedDetailId === String(selectedFlow.id)) {
        setSelectedDetailId(null);
      }
      setSelectedFlow(null);
    } catch {
      showNotification("error", t("common.api-error"));
      throw new Error("delete-failed");
    }
  }

  // Resolve the sheet's backing row from the current list page.
  const selectedSheetFlow = useMemo(() => {
    if (!selectedDetailId) return null;
    return (
      rows.find((row) => String(row.id) === selectedDetailId) ??
      selectedFlow ??
      null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDetailId, rows, selectedFlow]);

  // No per-row Edit / Delete columns — row click opens the sheet in
  // view mode; both affordances now live in the sheet header.
  const columns = useMemo<ColumnDef<FlowRow>[]>(() => [
    {
      accessorKey: "name",
      header: t("flows.items.title"),
    },
    {
      id: "flowTypeName",
      header: t("flows.items.type"),
      cell: ({ row }) => row.original.flowType.translatedName ?? "",
    },
    {
      id: "tags",
      header: t("flows.items.tags"),
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {(row.original.tags ?? []).map((tag) => (
            <Badge
              key={tag.id}
              style={{
                backgroundColor: tag.color ?? undefined,
                color: tag.color ? "#fff" : undefined,
              }}
            >
              {tag.name}
            </Badge>
          ))}
        </div>
      ),
    },
  ], [t]);

  const pageSize = flows?.range?.limit ?? 15;
  const pageCount = flows?.itemCount
    ? Math.max(1, Math.ceil(flows.itemCount / pageSize))
    : 1;

  return (
    <>
      <ConfirmDialog
        open={openDeleteFlowDialog}
        onOpenChange={(next) => { if (!next) setOpenDeleteFlowDialog(false); }}
        title={t("flows.delete-flow-dialog.title", {
          name: selectedFlow?.name,
        })}
        confirmLabel={t("common.delete")}
        onConfirm={onDeleted}
        isPending={isDeleting}
      />
      <AddFlowDialog
        title={t("flows.add-flow-dialog.title")}
        open={openAddFlowDialog}
        onClose={() => setOpenAddFlowDialog(false)}
        onCreated={() => setOpenAddFlowDialog(false)}
      />
      <FlowSheet
        value={selectedSheetFlow as FlowRowForSheet | null}
        open={selectedDetailId != null}
        onDelete={
          hasRemovePermission && selectedSheetFlow
            ? () => onDelete(selectedSheetFlow)
            : undefined
        }
        onOpenChange={(next) => {
          if (!next) {
            setSelectedDetailId(null);
            setSelectedFlow(null);
          }
        }}
        canEdit={hasEditPermission}
        initialEditing={editOnOpen}
        onEdited={() => {
          showNotification(
            "success",
            t("flows.edit-flow-dialog.success", "Flow updated"),
          );
        }}
      />
      <PageContainer title={t("flows.tab-title")}>
        <PageHeader
          title={t("flows.page-title")}
          description={t("flows.info-message")}
          actions={
            hasAddPermission ? (
              <Button onClick={() => setOpenAddFlowDialog(true)}>
                <PlusIcon className="size-4" />
                {t("flows.add-flow")}
              </Button>
            ) : null
          }
        />

        <div className="mt-6">
          <FiltersCard onSearch={onSearch} onCancel={onCancel}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <RemoteCombobox
                resource="flows/autocomplete"
                value={flow}
                onChange={setFlow}
                label={t("flows.filter.flow")}
              />
              <div className="space-y-1">
                <label
                  htmlFor="flow-type"
                  className="text-sm font-medium"
                >
                  {t("flows.filter.flow-type")}
                </label>
                <Select
                  key={`flowType-${clearKey}`}
                  value={flowType || undefined}
                  onValueChange={setFlowType}
                >
                  <SelectTrigger id="flow-type" className="w-full">
                    <SelectValue placeholder={t("flows.filter.flow-type")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">
                      {t("flows.add-flow-dialog.select-type.free")}
                    </SelectItem>
                    <SelectItem value="1">
                      {t("flows.add-flow-dialog.select-type.sequencial")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <TagsMultiSelect
                value={filterTags}
                onChange={setFilterTags}
                label={t("flows.filter.tags")}
                placeholder={t("tags.multi-select.placeholder")}
                mode="filter"
              />
            </div>
          </FiltersCard>
        </div>

        <div className="mt-6">
          <DataTable
            columns={columns}
            data={rows}
            emptyMessage={t("common.no-data", "No data")}
            onRowClick={onSee}
            loading={fetchingList}
            skeletonRows={pageSize}
            manualPagination={{
              pageIndex: page,
              pageCount,
              onPageChange: setPage,
            }}
          />
        </div>
      </PageContainer>
    </>
  );
}
